import {
  AdditiveAnimationBlendMode,
  AnimationMixer,
  AnimationUtils,
  LoopOnce,
  LoopPingPong,
  LoopRepeat,
  type AnimationAction,
  type AnimationActionLoopStyles,
  type AnimationClip,
  type Object3D,
} from 'three'

import {
  BLINK_DOUBLE_CHANCE,
  BLINK_DOUBLE_GAP_SECONDS,
  BLINK_INTERVAL_MAX_SECONDS,
  BLINK_INTERVAL_MIN_SECONDS,
} from '../config'
import type {
  AdditiveClipConfig,
  AnimationConfig,
  LoopMode,
  ReactionConfig,
} from '../content/manifest.types'

export type AnimationWarning =
  | {readonly kind: 'noClips'}
  | {readonly kind: 'clipNotFound'; readonly requested: string; readonly used: string}
  | {readonly kind: 'introClipNotFound'; readonly requested: string}
  | {readonly kind: 'blinkClipNotFound'; readonly requested: string}
  | {readonly kind: 'reactionClipNotFound'; readonly requested: string}
  | {readonly kind: 'greetingClipNotFound'; readonly requested: string}
  /**
   * An overlay pointed at the clip that is already playing as the main clip or
   * the intro. Not fatal - three.js will happily layer a clip on top of itself -
   * but the result is "the model moves twice as much", which reads as a rig bug
   * rather than as the campaign.json typo it is.
   *
   * Deliberately NOT extended to catch two overlays pointed at EACH OTHER
   * (`greeting.clip` === `reaction.clip` is the obvious next typo). Those two
   * can never run at the same time - `playReaction` declines while the greeting
   * is up - so the config is harmless rather than broken, and warning about a
   * harmless config is how a channel like this one gets ignored.
   */
  | {
      readonly kind: 'overlayClipReused'
      readonly layer: 'blink' | 'reaction' | 'greeting'
      readonly clip: string
    }

/** Live state, for `?debug=1` and `window.__probe`. Read through a getter. */
export interface AnimationDebug {
  readonly mainRunning: boolean
  readonly introRunning: boolean
  readonly blinking: boolean
  /** `null` when there is no blink clip, or before the layer is armed. */
  readonly secondsToNextBlink: number | null
  readonly doubleBlinkQueued: boolean
  /** True while ANY clip in the reaction pool is running. */
  readonly reacting: boolean
  /**
   * Which clip the last tap drew, `null` before the first one. Stays set after
   * the clip ends - pair it with `reacting` to tell "playing Bounce" from
   * "played Bounce a moment ago".
   *
   * Here because the draw is unseeded: without it a browser check can see THAT
   * a reaction ran but not WHICH, so "never the same clip twice in a row" would
   * be unobservable and therefore untestable.
   */
  readonly reactionClipName: string | null
  /** Clip time of the running reaction, 0 when none is. */
  readonly reactionTimeSeconds: number
  readonly greeting: boolean
  /** Clip time of the greeting, 0..greetingDuration. */
  readonly greetingTimeSeconds: number
  /**
   * `null` when there is no greeting clip, before the layer is armed, or once
   * this placement's greeting has been consumed.
   *
   * Unlike `secondsToNextBlink` there is no "next": the greeting is armed by
   * `playIntro()` and fires exactly once per placement, so `null` shortly after
   * a placement means "already greeted", not "broken". `greetingStartSeconds`
   * on the controller is what says whether it was ever going to fire at all.
   */
  readonly secondsToGreeting: number | null
}

export interface AnimationController {
  /** Drive from the pipeline module's existing THREE.Timer. */
  update(deltaSeconds: number): void
  /**
   * Plays the entrance clip from frame 0, handing over to the main clip when it
   * finishes. Call this the moment the model becomes visible - i.e. on
   * placement, not on load.
   *
   * No longer a *complete* no-op without an intro: it also re-arms the blink
   * layer and cancels any in-flight reaction, because this is the moment the
   * model becomes visible somewhere new.
   */
  playIntro(): void
  /**
   * Draws one clip from the reaction pool and layers it additively on top of
   * whatever is playing. Meant for "the user tapped the model itself"; tapping
   * the floor still re-places it.
   *
   * The draw excludes whatever played last, so two taps in a row never repeat.
   * It also pushes the blink countdown past the drawn clip - see the guard
   * comments in the implementation for why that is a correctness rule and not a
   * polish one.
   *
   * Returns the drawn clip's NAME, so the caller can key a per-clip cue - the
   * tap sound - off the same draw instead of guessing at it afterwards.
   * `null` when nothing started: an empty pool, one already running, the
   * entrance animation still owns the model, or the greeting does. The name is
   * also on `debug.reactionClipName`, which unlike this return value keeps
   * answering after the fact.
   */
  playReaction(): string | null
  /**
   * Forces the greeting now and consumes this placement's scheduled one, so a
   * forced bow is never chased by the timed one a moment later.
   *
   * Unguarded by `isIntroPlaying`, unlike `playReaction()`, and the asymmetry is
   * measured rather than sloppy: the reaction is blocked during the entrance
   * because `PropertyMixer._lerpAdditive` SUMS its `Root.scale` +0.0895 onto
   * whatever `Appear` currently is, and `Bow` moves no scale track at all (17 of
   * its 78 channels move, every one of them a rotation). A debug hook that
   * silently declines for the first 3.25 s of a placement is also a hook a
   * browser check has to time around instead of use.
   *
   * The reference campaign no longer CONFIGURES a greeting - its bow was baked
   * into `Appear` - so this returns `false` there and the paragraph above is the
   * contract for a campaign that does configure one. Kept unguarded rather than
   * quietly narrowed to match: the guard's reasoning is about the clip, not
   * about which campaign happens to be loaded.
   *
   * Returns `false` when nothing started - no greeting clip configured, or one
   * already running.
   */
  playGreeting(): boolean
  /**
   * Forces a blink now and re-arms the timer. The blink is otherwise on an
   * unseeded random schedule, so this is the only way a browser check can
   * observe one; also a legitimate hook if a campaign ever wants a cued wink.
   */
  playBlink(): boolean
  readonly clipName: string | null
  readonly clipDuration: number
  readonly introClipName: string | null
  readonly introDuration: number
  readonly blinkClipName: string | null
  readonly blinkDuration: number
  /**
   * The reaction pool, in the order `campaign.json` listed it, minus any name
   * that was not in the GLB. Empty means the model does not react at all - the
   * pointer handler reads exactly that to decide whether the silhouette is a tap
   * target or placement surface.
   *
   * A list rather than the old `reactionClipName` / `reactionDuration` pair
   * because there is no longer one answer, and reporting the first entry as if
   * there were is the kind of debug readout that can lie.
   */
  readonly reactionClips: readonly {readonly name: string; readonly duration: number}[]
  readonly greetingClipName: string | null
  readonly greetingDuration: number
  /**
   * Clip-seconds from `playIntro()` to the greeting's first frame - i.e.
   * `introDuration + delaySeconds`, or just `delaySeconds` when there is no
   * intro clip. `0` when there is no greeting.
   *
   * The one number that answers "when does it bow", and it lives here rather
   * than being recomputed by each debug surface because it is literally the
   * value written into the countdown - so it cannot disagree with the schedule
   * it claims to report.
   */
  readonly greetingStartSeconds: number
  /**
   * True while the entrance clip is still running. Read by the pointer handler:
   * during the intro the model is skinned near zero scale, so its tap volume
   * stands where it is GOING to be, and a tap must fall through to placement.
   */
  readonly isIntroPlaying: boolean
  readonly debug: AnimationDebug
  readonly warnings: readonly AnimationWarning[]
  dispose(): void
}

const LOOP_MODE: Record<LoopMode, AnimationActionLoopStyles> = {
  repeat: LoopRepeat,
  once: LoopOnce,
  pingpong: LoopPingPong,
}

/**
 * Builds a one-shot ADDITIVE layer from a clip authored against the rig's rest
 * pose rather than against the main clip's frame 0.
 *
 * `makeClipAdditive` rewrites every keyframe into a delta from the clip's own
 * frame 0 and flips `clip.blendMode`. `PropertyMixer.accumulateAdditive` then
 * starts from the additive identity - literally `0` for vectors
 * (`_setAdditiveIdentityNumeric`), the identity quaternion for rotations - and
 * `apply()` finishes with `accuN := accuN + additive accuN`. A track that does
 * not move in the source clip therefore contributes an EXACT zero, and the base
 * clip keeps the whole body no matter how far its pose is from the rest pose.
 *
 * That is the entire reason this is additive and not a cross-fade. In
 * CatMascot.glb `Blink`, `Happy`, `Bounce`, `Spin` and `Bow` each disagree with
 * `Idle` on 14 of their 78 channels at t = 0 (up to 0.0475 on a rotation
 * component) purely because Blender exported them against the rest pose. Played
 * as normal actions they would drag spine, chest, neck, ears, arms and tail to
 * the rest pose and back on every single trigger; played additively those
 * unmoved channels are worth exactly nothing and only the tracks the clip
 * actually animates survive - four for the blink, twenty-three for `Happy`,
 * twenty-two for `Spin` and nine for `Bounce`. (`Bow` moves seventeen and is
 * still in the GLB, but the reference campaign no longer layers it: its bow now
 * lives inside `Appear`.)
 *
 * `Spin` is also the one clip that turns `Root.rotation` - a full 360 degrees of
 * yaw. Additive rotation COMPOSES rather than sums, so it lands at the authored
 * angle over any base that does not itself turn the root, and `Idle` holds
 * `Root.rotation` at exactly identity for its whole 4 s. Measured over `Idle`:
 * 360.00 degrees of unwrapped yaw, back to an exact identity quaternion at the
 * end. Blender exports the second half of that turn in the opposite quaternion
 * hemisphere (a 1.98 jump between two adjacent keys at clip t = 0.958);
 * `Quaternion.slerpFlat` negates on a negative dot product, so it interpolates
 * as the continuous turn it is. Nothing here has to special-case it - but a
 * reader diffing the raw keyframes will see that jump and should know it is
 * expected.
 *
 * NOTE that additive blending on a scale track is an OFFSET, not a multiplier
 * (`PropertyMixer._lerpAdditive` is `buffer[j] += buffer[src] * t`). `Blink`'s
 * eye scale 1.0 -> 0.35 becomes a delta of -0.65, which lands correctly only
 * because the clip underneath holds that scale at exactly 1.
 *
 * That last clause used to be a statement about `Idle` alone, and it is now the
 * sharpest edge in this file. Two other clips animate the same eye scale:
 *
 *  - `Appear` (0.38 -> 1.0 over its first ~1.2 s). Safe, and the margin is the
 *    thing to preserve: `armBlink()` runs at `playIntro()` and the soonest draw
 *    is `BLINK_INTERVAL_MIN_SECONDS` = 3 s, by which point `Appear` has held the
 *    eye scale at exactly 1 since clip t = 1.25. Measured both ways in /debug -
 *    a blink forced at clip t = 3.0 and one forced at t = 4.0 (well into `Idle`)
 *    both land on 0.35167, identical to five decimal places. Lengthening the
 *    entrance past ~3 s of eye motion, or lowering BLINK_INTERVAL_MIN_SECONDS,
 *    is what would break it.
 *  - `Happy`, which squints to 0.05 - a delta of -0.95 held for 0.67 s. NOT safe
 *    on its own, because a blink can start at any moment and -0.95 + -0.65 puts
 *    the eye bone at a negative scale. `postponeBlink()` is what keeps those two
 *    apart; see its comment for the measurement.
 *
 * `makeClipAdditive` MUTATES the clip in place, which is why it is handed a
 * CLONE. GLTFLoader hands out fresh `AnimationClip`s per load, so mutating the
 * caller's array would be safe today - but nothing in this function's signature
 * says so, and building a second controller over the same clips (the debug
 * harness re-running, a hot reload, a future preview mode) would subtract the
 * reference pose twice. `AnimationClip.clone()` deep-copies the tracks and
 * carries `blendMode` through, and for these clips it is a few KB of
 * Float32Array.
 *
 * These actions dispatch `finished` on the SHARED mixer every time they end. The
 * intro handler's `event.action !== intro` guard is what stops that from
 * cross-talking - do not weaken it.
 */
const createAdditiveLayer = (mixer: AnimationMixer, clip: AnimationClip): AnimationAction => {
  const action = mixer.clipAction(
    AnimationUtils.makeClipAdditive(clip.clone()),
    undefined,
    // Redundant - the clone's own `blendMode` was just flipped and `clipAction`
    // reads it - but stated at the call site so the layer cannot silently become
    // a normal action if the clone ever stops carrying it.
    AdditiveAnimationBlendMode
  )
  action.setLoop(LoopOnce, 1)
  // Deliberately NOT `clampWhenFinished`. Clamping leaves the action
  // paused-but-enabled, re-evaluating 78 interpolants and accumulating a zero
  // delta every frame forever; letting it disable itself makes
  // `AnimationAction._update` return on its first line. Every overlay clip ends
  // on its own frame 0, so the clamped pose and the released one are the same
  // anyway - and with a pool of them the waste would be per-clip.
  action.clampWhenFinished = false
  // Activated once, here, and never stopped. `_activateAction` /
  // `_deactivateAction` reorder `AnimationMixer._bindings` and `_actions`, and
  // the one moment that must never happen is inside `mixer.update()`. Binding up
  // front removes the question entirely; `play()` on an already-active action is
  // a no-op, so the trigger path can call it freely.
  action.play()
  // ...and immediately inert. `enabled` IS the "is this layer running" flag from
  // here on: `reset()` sets it true, and LoopOnce without clamping sets it false
  // the frame the clip ends. No extra bookkeeping, and no `stop()` anywhere.
  action.enabled = false
  return action
}

/**
 * Resolves an overlay clip by name. Like `intro` and unlike `clip`, there is NO
 * first-clip fallback: an overlay pointed at an arbitrary clip layers that clip
 * on top of everything else, which looks like a broken rig rather than a typo.
 */
const findOverlayClip = (
  clips: readonly AnimationClip[],
  config: AdditiveClipConfig | null
): AnimationClip | null =>
  config === null ? null : (clips.find((candidate) => candidate.name === config.clip) ?? null)

/**
 * The pool version, for `reaction`. Same no-fallback rule as above, applied per
 * name, and it collects one warning per miss rather than one for the block.
 *
 * A missing name is DROPPED rather than fatal, and the pool carries on with what
 * is left. `scripts/check-assets.mjs` already fails the build on a name that is
 * not in the GLB, so anything reaching here got past the gate that exists to
 * catch it - at which point a model that still reacts with three of its four
 * clips beats one that has stopped reacting at all.
 */
const findOverlayClips = (
  clips: readonly AnimationClip[],
  config: ReactionConfig | null,
  warnings: AnimationWarning[]
): AnimationClip[] => {
  if (config === null) return []
  const found: AnimationClip[] = []
  for (const name of config.clips) {
    const clip = clips.find((candidate) => candidate.name === name)
    if (clip === undefined) {
      warnings.push({kind: 'reactionClipNotFound', requested: name})
      continue
    }
    found.push(clip)
  }
  return found
}

/**
 * Plays a loaded GLB's main clip, optionally preceded by a one-shot intro.
 *
 * With an intro configured NOTHING plays until `playIntro()` is called. That is
 * the point: an entrance animation is only meaningful while the model is on
 * screen, and `PlacementController` keeps it hidden until the first tap.
 *
 * `root` must be the object the clips were authored against - i.e. `gltf.scene`,
 * not a wrapper Group placed above it. GLTFLoader builds each track's name from
 * the target node's final `name`, and `PropertyBinding` resolves that name by
 * searching descendants of the mixer root, so the joints must be underneath it.
 *
 * Note that GLTFLoader runs every node name through
 * `PropertyBinding.sanitizeNodeName`, which strips `[ ] . : /`. Both sides of
 * the binding are sanitised so it resolves correctly, but the names in the
 * three.js scene graph do NOT match the names in the source file - `Ear.L`
 * becomes `EarL`, and a collision with another node of the same sanitised name
 * becomes `EarL_1`. Never hand-write a `getObjectByName` against DCC names.
 */
export const createAnimationController = (
  root: Object3D,
  clips: readonly AnimationClip[],
  config: AnimationConfig
): AnimationController => {
  const warnings: AnimationWarning[] = []

  // `noUncheckedIndexedAccess` types this as `AnimationClip | undefined`, so
  // the compiler forces the no-clip path to exist.
  const first = clips[0]

  if (first === undefined) {
    // A model with no animation is a content bug, not a runtime failure. Show
    // it standing still rather than failing the whole experience.
    warnings.push({kind: 'noClips'})
    return {
      update: () => {},
      playIntro: () => {},
      playReaction: () => null,
      playGreeting: () => false,
      playBlink: () => false,
      clipName: null,
      clipDuration: 0,
      introClipName: null,
      introDuration: 0,
      blinkClipName: null,
      blinkDuration: 0,
      reactionClips: [],
      greetingClipName: null,
      greetingDuration: 0,
      greetingStartSeconds: 0,
      isIntroPlaying: false,
      debug: {
        mainRunning: false,
        introRunning: false,
        blinking: false,
        secondsToNextBlink: null,
        doubleBlinkQueued: false,
        reacting: false,
        reactionClipName: null,
        reactionTimeSeconds: 0,
        greeting: false,
        greetingTimeSeconds: 0,
        secondsToGreeting: null,
      },
      warnings,
      dispose: () => {},
    }
  }

  let clip = first
  if (config.clip !== null) {
    const named = clips.find((candidate) => candidate.name === config.clip)
    if (named) {
      clip = named
    } else {
      warnings.push({kind: 'clipNotFound', requested: config.clip, used: first.name})
    }
  }

  let introClip: AnimationClip | null = null
  if (config.intro !== null) {
    // Unlike the main clip this does not fall back to `clips[0]` - playing an
    // arbitrary clip as an entrance animation is worse than playing none.
    introClip = clips.find((candidate) => candidate.name === config.intro?.clip) ?? null
    if (introClip === null) warnings.push({kind: 'introClipNotFound', requested: config.intro.clip})
  }

  const blinkClip = findOverlayClip(clips, config.blink)
  if (config.blink !== null && blinkClip === null) {
    warnings.push({kind: 'blinkClipNotFound', requested: config.blink.clip})
  }
  const reactionClips = findOverlayClips(clips, config.reaction, warnings)
  // `GreetingConfig extends AdditiveClipConfig`, so the same resolver takes it
  // with no signature change - which is the whole reason it extends rather than
  // being a parallel shape.
  const greetingClip = findOverlayClip(clips, config.greeting)
  if (config.greeting !== null && greetingClip === null) {
    warnings.push({kind: 'greetingClipNotFound', requested: config.greeting.clip})
  }
  // Cheap guard for the one typo that produces a plausible-looking model rather
  // than an obviously broken one: pointing an overlay at the clip that is
  // already playing doubles its motion instead of failing.
  for (const [layer, overlay] of [
    ['blink', blinkClip],
    // Every pool entry gets checked, not just the first - the typo this exists
    // to catch is per-name, and a four-clip pool is four chances to make it.
    ...reactionClips.map((overlay) => ['reaction', overlay] as const),
    ['greeting', greetingClip],
  ] as const) {
    if (overlay !== null && (overlay.name === clip.name || overlay.name === introClip?.name)) {
      warnings.push({kind: 'overlayClipReused', layer, clip: overlay.name})
    }
  }

  const mixer = new AnimationMixer(root)
  mixer.timeScale = config.timeScale

  const main = mixer.clipAction(clip)
  main.setLoop(LOOP_MODE[config.loop], Infinity)
  // Hold the last frame instead of snapping back to the bind pose.
  main.clampWhenFinished = config.loop === 'once'

  let intro: AnimationAction | null = null
  let onFinished: ((event: {readonly action: AnimationAction}) => void) | null = null

  const blink = blinkClip === null ? null : createAdditiveLayer(mixer, blinkClip)
  // One action per pool entry, built here and alive for the controller's
  // lifetime, exactly like the other two layers. `createAdditiveLayer` needs no
  // changes for N of them: each gets its own `clip.clone()`, its own single
  // `play()`, and is controlled only through `enabled` from here on.
  const reactions = reactionClips.map((reactionClip) => createAdditiveLayer(mixer, reactionClip))
  const greeting = greetingClip === null ? null : createAdditiveLayer(mixer, greetingClip)

  let introRunning = false
  /** Index into `reactions` of the last clip drawn; `-1` before the first tap. */
  let lastReactionIndex = -1

  /**
   * Blink scheduling, in CLIP seconds.
   *
   * Driven by the same `deltaSeconds` the mixer gets - never `setTimeout`, never
   * `Date.now()`. `app-module.ts` runs everything off a `THREE.Timer` connected
   * to the Page Visibility API precisely so a backgrounded phone freezes the
   * model instead of fast-forwarding it; a wall-clock timer would keep counting
   * behind the lock screen and fire a burst of blinks the moment the user came
   * back, which is the exact bug the Timer exists to prevent.
   */
  let blinkArmed = false
  let secondsToBlink = 0
  let doubleBlinkQueued = false

  /**
   * Unseeded on purpose, unlike `SPARK_SEED`. The sparks are seeded so the Nth
   * placement always draws the same burst and the effect can be diffed frame by
   * frame; a blink is the opposite - being unpredictable is the whole point, and
   * nothing downstream has to reproduce one. The debug harness gets
   * `playBlink()` instead of determinism.
   */
  const nextBlinkDelay = (): number =>
    BLINK_INTERVAL_MIN_SECONDS +
    Math.random() * (BLINK_INTERVAL_MAX_SECONDS - BLINK_INTERVAL_MIN_SECONDS)

  /** Cancels anything in flight and restarts the countdown from a fresh draw. */
  const armBlink = (): void => {
    blinkArmed = blink !== null
    doubleBlinkQueued = false
    secondsToBlink = nextBlinkDelay()
    // `enabled = false`, never `stop()`: stopping would deactivate the action,
    // run `restoreOriginalState()` on any binding whose useCount hits zero and
    // reorder the mixer's `_bindings` / `_actions`. Disabling makes
    // `AnimationAction._update` return without evaluating anything, which is all
    // we want.
    if (blink !== null) blink.enabled = false
  }

  /**
   * Pushes the blink countdown past a clip that is closing the eyes itself.
   *
   * This is a correctness rule, not polish. `Blink` and the reaction are both
   * additive and `PropertyMixer._lerpAdditive` SUMS scale deltas, so two clips
   * that both shrink `Eye.L/R` compose instead of taking the deeper one. The
   * reference model measures -0.950 on `Happy` (held from clip t = 0.46 to 1.13)
   * against -0.650 at the bottom of `Blink`: together that is 1 - 0.950 - 0.650
   * = -0.60, a NEGATIVE scale, and the eye marks render inside out at 60% while
   * the character is supposed to be smiling. Blink runs every 3-6 s and `Happy`
   * holds its squint for two thirds of a second, so this lands on roughly one
   * tap in five - often enough to be the first thing anyone reports.
   *
   * Unlike `armBlink()` this does NOT cancel a blink already in flight, and the
   * asymmetry is measured. `Blink` is 0.333 s and `Happy` does not pass -0.9
   * until clip t = 0.44, so a blink already running when the tap lands is always
   * over before the deep phase arrives; cancelling it would snap the eyes open
   * on the tap frame to avoid a collision that cannot happen. A QUEUED double
   * blink is dropped, because that one has not started and would fire
   * `BLINK_DOUBLE_GAP_SECONDS` from now - inside the window.
   *
   * Applied to every clip in the pool rather than only the ones that touch the
   * eyes. Neither `Bounce` nor `Spin` does, and they cost at most 1.25 s and
   * 2.0 s of postponed blinking respectively; knowing which clips move which
   * bones is exactly the thing this file has no business inferring at runtime.
   * The worst case is the LONGEST clip in the pool, so it grows with the pool -
   * at 2.0 s against a 3-6 s blink interval there is still room, but a pool
   * whose longest clip approached `BLINK_INTERVAL_MIN_SECONDS` would start
   * suppressing blinks rather than merely deferring one.
   */
  const postponeBlink = (seconds: number): void => {
    if (!blinkArmed) return
    doubleBlinkQueued = false
    // CLIP seconds on both sides: the countdown is decremented by
    // `deltaSeconds * config.timeScale`, and a clip's `duration` is in the same
    // units - the same pairing `triggerBlink` relies on for the double gap.
    secondsToBlink = Math.max(secondsToBlink, seconds)
  }

  const triggerBlink = (): boolean => {
    if (blink === null) return false
    blink.reset().play()
    if (doubleBlinkQueued) {
      doubleBlinkQueued = false
      secondsToBlink = nextBlinkDelay()
      return true
    }
    doubleBlinkQueued = Math.random() < BLINK_DOUBLE_CHANCE
    // The gap is measured from the START of this blink, so it must clear the
    // clip or the second `reset()` cuts the first one off mid-squash and the
    // eyes snap open. Expressed as the clip's OWN duration plus a pause, not as
    // an absolute: a swapped-in model with a slower blink still reads as a
    // double blink rather than as a stutter - the same bargain
    // `targetHeightMeters` makes about size.
    secondsToBlink = doubleBlinkQueued
      ? blink.getClip().duration + BLINK_DOUBLE_GAP_SECONDS
      : nextBlinkDelay()
    return true
  }

  /**
   * Draws uniformly from the pool MINUS whatever played last, so no two taps in
   * a row repeat. Maps `[0, n-2)` onto `[0, n) \ {last}` by stepping over the
   * excluded index rather than re-rolling, which keeps it constant time and
   * exactly uniform over the n-1 survivors.
   *
   * Unseeded, for the reason `nextBlinkDelay` above spells out: the point is
   * that the user cannot predict it, and nothing downstream has to reproduce a
   * draw. `debug.reactionClipName` is what makes it observable instead.
   *
   * A one-clip pool returns 0 without consulting the rule. Applying "never the
   * same twice" to a pool of one would mean never playing anything at all,
   * which is the difference between a constraint and a deadlock.
   */
  const pickReactionIndex = (): number => {
    if (reactions.length === 1) return 0
    if (lastReactionIndex < 0) return Math.floor(Math.random() * reactions.length)
    const offset = Math.floor(Math.random() * (reactions.length - 1))
    return offset < lastReactionIndex ? offset : offset + 1
  }

  /**
   * Clip-seconds from `playIntro()` to the greeting's first frame.
   *
   * Computed once, here, and read by both `armGreeting()` and the controller's
   * `greetingStartSeconds` - a re-derivation in a debug surface could drift from
   * the schedule it claims to report, and a debug readout that can lie is worse
   * than none.
   */
  const greetingStartSeconds =
    greeting === null ? 0 : (introClip?.duration ?? 0) + (config.greeting?.delaySeconds ?? 0)

  /**
   * Greeting scheduling, in CLIP seconds. The same clock as the blink countdown
   * above and for the same reasons - never `setTimeout`, never `Date.now()`.
   *
   * Armed at `introDuration + delaySeconds` from `playIntro()`, rather than at
   * `delaySeconds` from the intro's `finished` event. The two are the same
   * instant arithmetically: `AnimationMixer.update` advances the intro action by
   * `deltaTime *= this.timeScale`, this countdown is decremented by
   * `deltaSeconds * config.timeScale`, and `mixer.timeScale` IS
   * `config.timeScale` (set once at construction) - so both cross their
   * thresholds off the same accumulated sum, frame for frame. What differs is
   * the failure modes, and all three favour arming from `playIntro()`:
   *
   *  - `finished` only exists when there IS an intro action. A model with no
   *    intro, or with an `intro.clip` that is not in the GLB, would silently
   *    never greet - the second case being a typo that already has its own
   *    warning and does not need a second, invisible symptom. Arming from
   *    `playIntro()` measures from the placement instead, with no special case:
   *    `introClip?.duration ?? 0`.
   *  - Re-placement has to CANCEL a pending greeting, and `playIntro()` is the
   *    only thing that knows a re-placement happened. Arming from `finished`
   *    would need a disarm here anyway - two sites for one countdown, which is
   *    how the two drift apart.
   *  - The trigger calls `AnimationAction.reset()`, which must never run from
   *    inside `mixer.update()`'s action loop. Counting down in `update()` before
   *    `mixer.update()` puts it structurally outside; arming from `finished`
   *    would put the arithmetic inside it - legal, since a number write touches
   *    no mixer state, but it makes "nothing happens in that handler" one
   *    exception weaker in exchange for nothing.
   *
   * Fires ONCE per placement. `greetArmed` is cleared by the trigger and only
   * `armGreeting()` sets it again; that is the single behavioural difference
   * from the blink countdown, which re-arms itself forever.
   */
  let greetArmed = false
  let secondsToGreet = 0

  /**
   * Cancels a greeting in flight and restarts the countdown from the top.
   *
   * `enabled = false` mid-clip drops a non-zero additive delta to zero in one
   * frame, which everywhere else in this file is the thing to avoid. It is right
   * here for the same reason the reaction is cancelled alongside it in
   * `playIntro()`: this runs at the instant the model teleports somewhere new
   * and restarts the entrance from a near-zero scale. There is no pose worth
   * preserving, and a bow that survived the teleport would be playing where
   * nobody is looking.
   */
  const armGreeting = (): void => {
    greetArmed = greeting !== null
    secondsToGreet = greetingStartSeconds
    // `enabled = false`, never `stop()` - see `armBlink` above.
    if (greeting !== null) greeting.enabled = false
  }

  const triggerGreeting = (): boolean => {
    if (greeting === null) return false
    // Disarmed BEFORE the early return below, and unconditionally. The greeting
    // is one beat per placement whether it played or was declined; leaving the
    // countdown armed after a declined attempt would retry it every frame until
    // the running one ended, and then fire a second, unscheduled bow.
    greetArmed = false
    // Ignored, not restarted - the reason `playReaction` spells out below.
    // `Bow` opens and closes on its own frame 0 (measured: 0 of 78 channels
    // differ), so a running one sits at a NON-zero delta and `reset()` would
    // drop seventeen rotation channels to zero in a single frame.
    if (greeting.enabled) return false
    greeting.reset().play()
    return true
  }

  if (introClip === null) {
    // No entrance animation: the main clip owns the model from load onwards,
    // which is the behaviour every model had before intros existed.
    main.play()
    // Same beat: with no intro the model is live from load, so the blink layer
    // is live from load too. With an intro, `playIntro()` arms it instead.
    armBlink()
    // Always next to `armBlink()`, in both of the two places that call it, so a
    // reader never has to work out why one of the two countdowns is missing from
    // one of them.
    armGreeting()
  } else {
    const crossFadeSeconds = config.intro?.crossFadeSeconds ?? 0
    intro = mixer.clipAction(introClip)
    intro.setLoop(LoopOnce, 1)
    // Holds the final pose across the frame between `finished` firing and the
    // main action's first update, so nothing flashes back to the bind pose.
    intro.clampWhenFinished = true

    onFinished = (event) => {
      // Load-bearing, and now doing double duty: the blink and reaction layers
      // dispatch `finished` on this same mixer every time they end, and without
      // this guard every blink would restart the intro -> main handover.
      if (event.action !== intro) return
      introRunning = false
      main.reset().play()
      // Deliberately a cross-fade rather than `intro.stop()`. `finished` is
      // dispatched from inside the action-update loop of `mixer.update()`,
      // BEFORE the bindings are applied; `stop()` would reorder
      // `AnimationMixer._bindings` mid-flight and can drop this frame's pose to
      // the bind pose. `crossFadeTo` only installs weight interpolants, so it
      // touches no mixer state that is being iterated.
      intro?.crossFadeTo(main, crossFadeSeconds, false)
    }
    mixer.addEventListener('finished', onFinished)
  }

  return {
    update: (deltaSeconds) => {
      // BEFORE `mixer.update`, and never from a mixer event. The trigger calls
      // `AnimationAction.reset()`; doing that from inside the mixer's own action
      // loop is the same family of bug the `finished` handler above is written
      // around. Here we are safely outside it.
      if (blinkArmed && blink !== null) {
        // Scaled into clip time. `AnimationMixer.update` does
        // `deltaTime *= this.timeScale` before touching any action, so the blink
        // cadence has to move with it or a `timeScale: 2` model would animate at
        // double speed while blinking at its original pace.
        secondsToBlink -= deltaSeconds * config.timeScale
        if (secondsToBlink <= 0) triggerBlink()
      }
      // A second, independent countdown. It shares no state with the blink one
      // and there is no meaningful order between them - but both are before
      // `mixer.update` for the identical reason stated above.
      if (greetArmed && greeting !== null) {
        // Scaled into clip time, same as the blink. Without it a `timeScale: 2`
        // model would finish a 3.25 s entrance in 1.625 s of wall clock and then
        // stand there for another 1.775 s before greeting.
        secondsToGreet -= deltaSeconds * config.timeScale
        if (secondsToGreet <= 0) triggerGreeting()
      }
      mixer.update(deltaSeconds)
    },

    playIntro: () => {
      // Before the early return below, so this runs even with no intro
      // configured. This is the instant the model becomes visible at a new spot;
      // a blink counted down while it sat behind `placement-root.visible = false`
      // would otherwise land on the tap itself.
      armBlink()
      // Alongside `armBlink()` and for the same reason: this is the instant the
      // character arrives somewhere new, and the greeting is a beat measured
      // from it. Deliberately NOT a fifth line in app-module's `revealPlaced()`
      // - that block is the four things that must restart on ONE frame
      // together, and the greeting is a consequence of one of them rather than
      // a member of the set.
      armGreeting()
      // A reaction from the previous placement has no business surviving the
      // model teleporting somewhere else. `lastReactionIndex` goes with it: the
      // no-repeat rule is about two consecutive taps, and a re-placement ends
      // that run the same way it ends the clip.
      for (const layer of reactions) layer.enabled = false
      lastReactionIndex = -1
      if (intro === null) return
      introRunning = true
      main.stop()
      intro.reset().play()
      // Apply frame 0 immediately. An entrance animation typically starts at a
      // near-zero scale, so without this the first frame after the tap renders
      // the model at full size before snapping down on the next update.
      mixer.update(0)
    },

    playReaction: () => {
      if (reactions.length === 0) return null
      // Ignored, not restarted. Every overlay clip opens and closes on its own
      // frame 0 - measured, exactly 0.0 on all 78 channels - so a running
      // reaction sits at a NON-zero delta, and `reset()` would drop it to zero
      // in a single frame: a visible pop, worst case the +0.0895 on `Root.scale`
      // or `Spin`'s 180 degrees of `Root.rotation`. A 1.25-2.0 s reaction is
      // short enough that swallowing the second tap reads as "already reacting"
      // rather than as a dropped input.
      //
      // ANY of them, never just the one that is about to be drawn. Two pool
      // clips running at once is the same composition hazard as the two guards
      // below, arrived at from inside the feature: `Happy`, `Bounce` and `Spin`
      // all move `Root.scale` (+0.0895, +0.07, and -0.0599/+0.0299) and all move
      // Neck, Ear.L/R, UpperArm.L/R and Tail2/3/4 - `Spin` covers all nine of
      // `Bounce`'s channels - and `_lerpAdditive` sums while `_slerpAdditive`
      // composes.
      if (reactions.some((layer) => layer.enabled)) return null
      // Not during the entrance, and it now carries THREE reasons rather than
      // one.
      //
      // The original: `PropertyMixer._lerpAdditive` is `buffer[j] += buffer[src]
      // * t` - a SUM, not a ratio - so the reaction's +0.0895 on `Root.scale`
      // lands on whatever `Appear` currently is. At its 0.05 starting scale that
      // is a 2.8x pop instead of a 9% one. `Spin` makes that half of the
      // argument WORSE rather than milder: its `Root.scale` delta is the first
      // in the pool to go NEGATIVE (-0.0599 on y, held around clip t = 0.29), so
      // on `Appear`'s frame 0 the sum is 0.05 - 0.06 = -0.01 and the whole model
      // renders inside out at a hair's width. A pop is ugly; this is broken.
      //
      // That argument alone no longer covers the whole clip and MUST NOT be used
      // to narrow this guard. `Appear` is 3.25 s and its `Root.scale` is exactly
      // 1 from clip t = 1.25, so the scale hazard is real for the first 38% and
      // harmless after it. What covers the rest is the guard below, relocated:
      // the bow now lives INSIDE `Appear` (clip t = 0.75 to the end, measured
      // against the standalone `Bow` at an offset of 0.751), and it moves eight
      // of `Bounce`'s nine channels, eight of `Happy`'s twenty-three and eight
      // of `Spin`'s twenty-two - Neck, Ear.L/R, UpperArm.L/R, Tail2/3/4.
      // `_slerpAdditive` COMPOSES those rotation deltas rather than averaging
      // them. So "the model is at full scale, this can be allowed now" is
      // exactly the wrong conclusion: full scale is when the bow is deepest.
      //
      // `Appear` also drives `Eye.L/R.scale` (from 0.38 up to 1.0), which the
      // current `Happy` squints to 0.05 - the same summing hazard `postponeBlink`
      // exists for, on a third clip. One more reason this guard only ever gets
      // wider.
      if (introRunning) return null
      // Not during the greeting either, and this one is about CHANNELS rather
      // than about scale. Eight of the channels the pool moves - Neck, Ear.L/R,
      // UpperArm.L/R and Tail2/3/4 - are also moved by `Bow`, and
      // `PropertyMixer._slerpAdditive` COMPOSES rotation deltas rather than
      // averaging them, so the two running together bend the neck and tail well
      // past anything either clip authored. `Bow` carries roughly four times the
      // amplitude it used to (0.1975 against 0.0274 on its largest channel), so
      // this is a worse collision than when the guard was written, not a milder
      // one. That is the same "the model moves twice as much, which reads as a
      // rig bug" failure `overlayClipReused` exists for, arrived at from the
      // other direction, and no warning could catch this one because both clips
      // are correctly configured.
      //
      // Blocked rather than stacked, and blocked rather than cancelling the
      // greeting: cancelling would snap those same seventeen channels to zero
      // mid-arc, which is the pop the "ignored, not restarted" rule above
      // exists to prevent. Swallowing the tap is already what this function
      // does to a second tap during a reaction, and two seconds of a character
      // visibly bowing reads as "busy greeting you" rather than as lost input.
      //
      // Unreachable under the reference campaign, which configures no greeting
      // at all - but the collision it describes did not go away, it MOVED into
      // `Appear`, where the `introRunning` guard above now catches it. Kept for
      // the campaign that layers a bow again rather than deleted as dead code.
      if (greeting?.enabled === true) return null

      const index = pickReactionIndex()
      const layer = reactions[index]
      if (layer === undefined) return null
      lastReactionIndex = index
      const drawn = layer.getClip()
      // No `mixer.update(0)` here, unlike `playIntro()`. An additive clip's
      // frame 0 is exactly zero delta by construction, so there is nothing to
      // apply early and nothing to flash.
      layer.reset().play()
      // After the clip starts, so it can be sized from the one that actually
      // won the draw rather than from the longest in the pool. See
      // `postponeBlink` for why a blink landing inside this window drives the
      // eye bones to a negative scale.
      postponeBlink(drawn.duration)
      return drawn.name
    },

    playGreeting: () => triggerGreeting(),

    playBlink: () => triggerBlink(),

    clipName: clip.name,
    clipDuration: clip.duration,
    introClipName: introClip?.name ?? null,
    introDuration: introClip?.duration ?? 0,
    blinkClipName: blinkClip?.name ?? null,
    blinkDuration: blinkClip?.duration ?? 0,
    reactionClips: reactionClips.map(({name, duration}) => ({name, duration})),
    greetingClipName: greetingClip?.name ?? null,
    greetingDuration: greetingClip?.duration ?? 0,
    greetingStartSeconds,

    get isIntroPlaying() {
      return introRunning
    },

    get debug(): AnimationDebug {
      return {
        mainRunning: main.isRunning(),
        introRunning,
        blinking: blink?.enabled ?? false,
        secondsToNextBlink: blinkArmed ? Math.max(secondsToBlink, 0) : null,
        doubleBlinkQueued,
        reacting: reactions.some((layer) => layer.enabled),
        // The DRAWN clip, whether or not it is still running - `reacting` above
        // is what says which. Reading it off `lastReactionIndex` rather than off
        // whichever layer happens to be enabled keeps it readable after the clip
        // ends, which is when a browser check gets to look.
        reactionClipName: reactions[lastReactionIndex]?.getClip().name ?? null,
        reactionTimeSeconds: reactions.find((layer) => layer.enabled)?.time ?? 0,
        greeting: greeting?.enabled ?? false,
        greetingTimeSeconds: greeting?.time ?? 0,
        secondsToGreeting: greetArmed ? Math.max(secondsToGreet, 0) : null,
      }
    },

    warnings,
    dispose: () => {
      // Still one listener: the additive layers deliberately do NOT register
      // their own `finished` handler - they end by disabling themselves - so
      // there is nothing new to unregister here.
      if (onFinished !== null) mixer.removeEventListener('finished', onFinished)
      mixer.stopAllAction()
      // Drops every action bound to this root, the additive ones included. Their
      // clips are clones owned by this closure and become unreachable with it;
      // `gltf.animations` is left untouched, which is what makes building a
      // second controller over the same clips safe.
      mixer.uncacheRoot(root)
    },
  }
}
